import { siteApi } from "@api";
import {
  closeIframeDialog,
  hideDownloadBanner,
  openIframeDialog,
  updateIframeSrcDoc,
  updateIframeUrl,
} from "@components/utils/IframeDialog";
import { envConfig } from "@configs/env";
import { BRAND_DOMAINS } from "@constants/brand-domains";
import { Paths } from "@constants/paths";
import useServerInfo from "@hooks/useServerInfo";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import { APIErrorCodes, GameGatewayType, GameModel, GameProviderModel, useHandleApiResponse } from "kz-ui-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useGameDataSource } from ".";

interface UseGameGatewayOptions {
  // Callback when the game is successfully opened
  onSuccess?: () => void;
  // Callback when the game history is changed
  onRecentChange?: () => void;
  // Callback when starting to open the game
  onOpen?: () => void;
}

const useGameGateway = (options?: UseGameGatewayOptions) => {
  const brand = envConfig.brand.name as keyof typeof BRAND_DOMAINS;
  const brandDomain = BRAND_DOMAINS[brand] ? BRAND_DOMAINS[brand] : brand;

  const serverInfo = useServerInfo();

  const [openingGame, setOpeningGame] = useState<GameModel>();
  const { handleApiResponse } = useHandleApiResponse({ toast });
  const [getGameGateway, { isFetching: isFetchingGameGateway }] = siteApi.useLazyGetGameGatewayQuery();
  const { providersResponse: gameProviderList } = useGameDataSource();

  const dispatch = useDispatch();

  const refOnSuccess = useRef(options?.onSuccess);
  const { t } = useTranslation();

  useEffect(() => {
    refOnSuccess.current = options?.onSuccess;
  });

  const providerMap = useMemo(() => {
    const resultMap: Record<string, GameProviderModel> = {};
    gameProviderList?.entries?.forEach((provider) => {
      resultMap[provider.id] = provider;
    });
    return resultMap;
  }, [gameProviderList]);

  // Open redirect game
  const openRedirectGame = useCallback((url: string) => {
    updateIframeUrl(url);
    refOnSuccess.current?.();
  }, []);

  // Open native game
  const openNativeGame = useCallback((html: string) => {
    updateIframeSrcDoc(html);
    refOnSuccess.current?.();
  }, []);

  const handleOpenGame = useCallback(
    async (game: GameModel) => {
      setOpeningGame(game);
      options?.onOpen?.();
      hideDownloadBanner(true);
      // Open the game in an iframe dialog
      openIframeDialog(
        {
          // open empty iframe first
          url: null,
          title: <span className="font-semibold">{serverInfo?.label || serverInfo?.name || brandDomain}</span>,
        },
        {
          withLogo: true,
          withAppInstallerOffset: false,
          allowLandscapeMode: true,
          closeOnBack: true,
        },
      );
      // push a new state to the history stack, so that we can handle the back button
      window.history.pushState({ isGameOpen: true }, "");

      // redirect url on quit game
      const redirectUrl = `${window.location.origin}${Paths.PUBLIC.IFRAME_SHUTDOWN}`;

      // Get the game gateway
      const gameGatewayResponse = await getGameGateway({
        query: {
          code: game.providerRef,
          productId: providerMap[game.gameProviderId]?.sourceRef,
          lobbyUrl: redirectUrl,
        },
      });

      // Handle the game gateway response, update the iframe URL/HTML
      handleApiResponse(gameGatewayResponse, {
        onSuccess: (response) => {
          let isError = true;
          // Check type of game
          switch (response.type) {
            case GameGatewayType.REDIRECT:
              // Open iframe with 'src'
              if (response.url) {
                openRedirectGame(response.url);
                isError = false;
              }
              break;
            case GameGatewayType.NATIVE:
              // Open iframe with 'srcDoc'
              if (response.html) {
                openNativeGame(response.html);
                isError = false;
              }
              break;
            default:
              // fallback to redirect
              if (response.url) {
                openRedirectGame(response.url);
                isError = false;
              }
              break;
          }

          // close the iframe dialog if there is an error
          if (isError) {
            closeIframeDialog();
            toast.error(t("Game is not available at the moment. Please try again later."));
          }

          // refetch recent games
          dispatch(siteApi.util.invalidateTags(["game-recent"]));
        },
        defaultErrorMessage: t("Game is not available at the moment. Please try again later."),
        onError: () => {
          closeIframeDialog();
        },
        errorMessageResolver: (error) => {
          if (error.data?.error?.message === APIErrorCodes.Game.InActive) {
            return t("Game is not available at the moment. Please try again later.");
          }
          if (error.data?.error?.message === APIErrorCodes.Game.GameLoginFailed) {
            return t("please try again later");
          }
          return resolveAuthResponseError(error, t);
        },
      });
    },
    [
      options,
      brandDomain,
      getGameGateway,
      providerMap,
      handleApiResponse,
      t,
      dispatch,
      openRedirectGame,
      openNativeGame,
      serverInfo,
    ],
  );

  return {
    open: handleOpenGame,
    isFetching: isFetchingGameGateway,
    providerMap,
    openingGame,
  };
};

export default useGameGateway;
